import React from 'react';
import PropTypes from 'prop-types';

import { Divider, Grid, RadioCard, RadioGroup, Text, Title, UnitInput } from '@homeexchange/panorama';
import i18n from '../../../i18n';
import Home from '../../../models/Home';
import { currencies, DEFAULT_CURRENCY } from '../../currency';

const TRACKING_PROFESSIONAL_CLEANING_CHANGED = 'professional_cleaning_changed';

const CONTRIBUTION_OPTIONS = [
    { value: Home.PROFESSIONAL_CLEANING_CONTRIBUTION, label: 'common:yes' },
    { value: Home.PROFESSIONAL_CLEANING_OFFERED, label: 'home:page_professionalcleaning_offer_label' },
    { value: Home.PROFESSIONAL_CLEANING_TO_DISCUSS, label: 'home:page_professionalcleaning_itdepends_label' }
];

export const SECTION_ID = 'professional-cleaning';

export const isAmountMissing = (home) =>
    Boolean(home.get('has_professional_cleaner')) &&
    home.get('professional_cleaning_contribution') === Home.PROFESSIONAL_CLEANING_CONTRIBUTION &&
    !(parseFloat(home.get('professional_cleaning_amount')) > 0);

// Every answer flags the section as declared (the API then records the source), and the fields
// below the changed question are reset so the home never carries details the host can no longer see.
export const nextProfessionalCleaningFields = (home, name, value, defaultCurrency) => {
    const fields = {
        [name]: value,
        professional_cleaning_declared: true
    };

    if (name === 'has_professional_cleaner') {
        fields.professional_cleaning_contribution = value ? Home.PROFESSIONAL_CLEANING_OFFERED : null;
        fields.professional_cleaning_amount = null;
        fields.professional_cleaning_currency = null;
    }

    if (name === 'professional_cleaning_contribution') {
        const asksContribution = value === Home.PROFESSIONAL_CLEANING_CONTRIBUTION;
        fields.professional_cleaning_amount = null;
        fields.professional_cleaning_currency = asksContribution
            ? home.get('professional_cleaning_currency') || defaultCurrency
            : null;
    }

    return fields;
};

export default function HomeEditProfessionalCleaning({ home, user, onChange, showErrors }) {
    const hasProfessionalCleaner = Boolean(home.get('has_professional_cleaner'));
    const contribution = home.get('professional_cleaning_contribution') || Home.PROFESSIONAL_CLEANING_OFFERED;
    const defaultCurrency = user?.get('currency') || DEFAULT_CURRENCY;
    const showAmountError = showErrors && isAmountMissing(home);

    const change = (name, value) => {
        onChange(
            null,
            { professional_cleaning: nextProfessionalCleaningFields(home, name, value, defaultCurrency) },
            { name, value, track: TRACKING_PROFESSIONAL_CLEANING_CHANGED }
        );
    };

    return (
        <div className="form-group" id={SECTION_ID}>
            <Divider className="pano-my-16" />
            <Title as="h3" variant="title-xsmall">
                {i18n.t('global:global_professionalcleaning')}
            </Title>

            <Text as="p" id="professional-cleaning-question" variant="label-large" className="pano-mb-16">
                {i18n.t('home:page_professionalcleaning_cleaningquestion_title')}
            </Text>
            <RadioGroup
                aria-labelledby="professional-cleaning-question"
                name="has_professional_cleaner"
                value={hasProfessionalCleaner ? '1' : '0'}
                onValueChange={(value) => change('has_professional_cleaner', value === '1')}
            >
                <Grid columns={{ base: '1', tablet: '2' }} gap="12">
                    <RadioCard layout="vertical" label={i18n.t('common:yes')} value="1" />
                    <RadioCard layout="vertical" label={i18n.t('common:no')} value="0" />
                </Grid>
            </RadioGroup>

            {hasProfessionalCleaner && (
                <>
                    <Text
                        as="p"
                        id="professional-cleaning-contribution"
                        variant="label-large"
                        className="pano-mt-16"
                    >
                        {i18n.t('home:page_professionalcleaning_contributionquestion_title')}
                    </Text>
                    <Text as="p" variant="body-small" thin color="secondary" className="pano-mb-16">
                        {i18n.t('home:page_professionalcleaning_contributionquestion_text')}
                    </Text>
                    <RadioGroup
                        aria-labelledby="professional-cleaning-contribution"
                        name="professional_cleaning_contribution"
                        value={contribution}
                        onValueChange={(value) => change('professional_cleaning_contribution', value)}
                    >
                        <Grid columns={{ base: '1', tablet: '2' }} gap="12">
                            {CONTRIBUTION_OPTIONS.map((option) => (
                                <RadioCard
                                    key={option.value}
                                    layout="vertical"
                                    label={i18n.t(option.label)}
                                    value={option.value}
                                />
                            ))}
                        </Grid>
                    </RadioGroup>
                </>
            )}

            {hasProfessionalCleaner && contribution === Home.PROFESSIONAL_CLEANING_CONTRIBUTION && (
                <>
                    <Text as="p" variant="label-large" className="pano-mt-16">
                        {i18n.t('home:page_professionalcleaning_pricequestion_title')}
                    </Text>
                    <Text as="p" variant="body-small" thin color="secondary" className="pano-mb-16">
                        {i18n.t('home:page_professionalcleaning_pricequestion_text')}
                    </Text>
                    <UnitInput
                        id="professional_cleaning_amount"
                        name="professional_cleaning_amount"
                        aria-label={i18n.t('home:page_professionalcleaning_pricequestion_title')}
                        value={home.get('professional_cleaning_amount')?.toString() ?? ''}
                        onValueChange={(value) =>
                            change('professional_cleaning_amount', value === '' ? null : value)
                        }
                        errorMessage={
                            showAmountError
                                ? i18n.t('home:page_professionalcleaning_pricequestion_error')
                                : undefined
                        }
                        units={Object.keys(currencies).map((code) => ({ value: code, label: code }))}
                        unitLabel={i18n.t('common:currency')}
                        unit={home.get('professional_cleaning_currency') || defaultCurrency}
                        onUnitChange={(value) => change('professional_cleaning_currency', value)}
                        isFullWidth
                    />
                </>
            )}
            <Divider className="pano-my-16" />
        </div>
    );
}

HomeEditProfessionalCleaning.propTypes = {
    home: PropTypes.instanceOf(Home).isRequired,
    user: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    showErrors: PropTypes.bool
};
